import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  mapAccountBriefingsToViews,
  type MappedWatchListPayload,
  type XFactorAccountBriefingsResponse,
} from "../../Pages/TempViewsRendering/lib/watchListMapper";
import { post } from "../../Services/ApiRequest";
import type { XFactorPortfolioAccount } from "../../Types/XFactorViewsRendering";
import { API_URLS } from "../../Utils/Constants/APIConstants";

/**
 * State for the Temp Views Rendering tab.
 *
 * Loads via POST /account-briefings for explicit EB account ids only.
 */

const unwrap = (res: any) => res?.content ?? res;

const message = (error: any, fallback: string) =>
  error?.response?.data?.detail || error?.message || fallback;

interface XFactorViewsState {
  viewId: string;
  freshnessDays: number;
  orgId: string | null;
  accountBriefings: {
    data: XFactorAccountBriefingsResponse | null;
    accounts: XFactorPortfolioAccount[];
    briefingDate: string | null;
    loading: boolean;
    error: string | null;
  };
  payloads: Record<string, MappedWatchListPayload>;
}

const initialState: XFactorViewsState = {
  viewId: "execbridge-flow",
  freshnessDays: 7,
  orgId: null,
  accountBriefings: {
    data: null,
    accounts: [],
    briefingDate: null,
    loading: false,
    error: null,
  },
  payloads: {},
};

export const fetchXFactorAccountBriefingsViews = createAsyncThunk(
  "xfactorViews/accountBriefings",
  async (
    args: {
      account_ids: string[];
      freshnessDays?: number;
      accountNames?: Record<string, string | null | undefined>;
    },
    { rejectWithValue }
  ) => {
    const ids = Array.from(
      new Set(
        args.account_ids.map(id => String(id || "").trim()).filter(Boolean)
      )
    );
    if (!ids.length) {
      return rejectWithValue("No account IDs provided");
    }
    try {
      const { accountNames } = args;
      const res: XFactorAccountBriefingsResponse = unwrap(
        await post(API_URLS.xfactorAccountBriefings(), {
          account_ids: ids,
          include_payload: true,
        })
      );
      return {
        response: res,
        orderedAccountIds: ids,
        accountNames: accountNames ?? {},
        freshnessDays: args.freshnessDays ?? 7,
      };
    } catch (error: any) {
      return rejectWithValue(
        message(error, "Failed to load account briefings")
      );
    }
  }
);

const XFactorViewsSlice = createSlice({
  name: "xfactorViews",
  initialState,
  reducers: {
    setXFactorView: (state, action) => {
      state.viewId = action.payload;
    },
    setXFactorFreshnessDays: (state, action) => {
      state.freshnessDays = action.payload;
    },
    setXFactorOrg: (state, action) => {
      state.orgId = action.payload;
      state.accountBriefings = initialState.accountBriefings;
      state.payloads = {};
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchXFactorAccountBriefingsViews.pending, state => {
        state.accountBriefings.loading = true;
        state.accountBriefings.error = null;
      })
      .addCase(fetchXFactorAccountBriefingsViews.fulfilled, (state, action) => {
        const { response, freshnessDays, orderedAccountIds, accountNames } =
          action.payload;
        const mapped = mapAccountBriefingsToViews(
          response,
          freshnessDays,
          orderedAccountIds,
          accountNames
        );
        state.accountBriefings.loading = false;
        state.accountBriefings.data = response;
        state.accountBriefings.accounts = mapped.accounts;
        state.accountBriefings.briefingDate = mapped.briefingDate;
        state.accountBriefings.error = null;
        state.payloads = mapped.payloads;
      })
      .addCase(fetchXFactorAccountBriefingsViews.rejected, (state, action) => {
        state.accountBriefings.loading = false;
        state.accountBriefings.error = action.payload as string;
        state.accountBriefings.accounts = [];
        state.accountBriefings.data = null;
        state.payloads = {};
      });
  },
});

export const { setXFactorView, setXFactorFreshnessDays, setXFactorOrg } =
  XFactorViewsSlice.actions;
export default XFactorViewsSlice.reducer;
